const { normalizeConditionVariablePath } = require('./flowConditionVariables');

const schemaTypeTokens = (type) => String(type || '')
  .toLowerCase()
  .split(/[^a-z0-9_]+/)
  .filter(Boolean);

const isArrayLikeSchemaType = (type) => schemaTypeTokens(type).includes('array');
const IGNORED_START_VARIABLE_PATHS = new Set([
  'payload',
  'protocol',
  'root',
  'root.payload',
  'root.protocol'
]);

const schemaTypeLabel = (type) => {
  const normalized = String(type || '').toLowerCase();
  if (isArrayLikeSchemaType(type)) return 'lista';
  if (['int', 'integer', 'number', 'float', 'double'].includes(normalized)) return 'numero';
  if (['bool', 'boolean'].includes(normalized)) return 'booleano';
  if (['object', 'json'].includes(normalized)) return 'objeto';
  return normalized || 'valor';
};

const stripExecutionArrayPrefix = (value = '') => {
  const path = String(value || '').trim();
  if (!path) return '';
  if (path === '[]' || path === '[0]' || path === '0') return '';
  return path
    .replace(/^\[\]\.?/, '')
    .replace(/^\[0\]\.?/, '')
    .replace(/^0\./, '')
    .trim();
};

const stripExecutionRecordPath = (value = '', recordPath = '') => {
  const path = stripExecutionArrayPrefix(value);
  const selectedPath = String(recordPath || '').trim().replace(/^\$\.?/, '');
  if (!selectedPath) return path;
  const escapedPath = selectedPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return path
    .replace(new RegExp(`^${escapedPath}(?:\\[\\])?(?:\\.|$)`), '')
    .replace(/^\./, '');
};

const normalizeExecutionStartVariableOptions = (variables = [], recordPath = '') => {
  const entries = Array.isArray(variables) ? variables : [];
  const mapped = new Map();
  const normalizedRecordPath = normalizeConditionVariablePath(recordPath || '');
  const shouldExposeRootNamespace = Boolean(normalizedRecordPath);

  const normalizeArrayPathCandidate = (value) => {
    const text = String(value ?? '').trim();
    const normalized = normalizeConditionVariablePath(text);
    if (!normalized && text === '') return '';
    return normalized || null;
  };

  const registerOption = (path, variable, arrayPaths = []) => {
    const normalizedValue = normalizeConditionVariablePath(path);
    if (!normalizedValue) return;
    if (IGNORED_START_VARIABLE_PATHS.has(normalizedValue)) return;
    const normalizedType = String(variable?.type || '').trim();
    const normalizedArrayPaths = [...new Set((Array.isArray(arrayPaths) ? arrayPaths : [])
      .map((item) => normalizeArrayPathCandidate(item))
      .filter((item) => item !== null))];
    const normalized = {
      ...variable,
      value: normalizedValue,
      label: `${normalizedValue} (${schemaTypeLabel(normalizedType)})`,
      arrayPaths: normalizedArrayPaths
    };
    const existing = mapped.get(normalizedValue);
    if (!existing) {
      mapped.set(normalizedValue, normalized);
      return;
    }
    if (existing.group !== 'configured' && normalized.group === 'configured') {
      mapped.set(normalizedValue, normalized);
    }
  };

  entries.forEach((variable) => {
    const rawValue = String(variable?.value || '');
    const normalizedSourcePath = normalizeConditionVariablePath(rawValue);
    if (!normalizedSourcePath) return;
    const fromRootArrayNotation = /^(?:\[\]|\[0\]|0)(?:\.|$)/.test(rawValue);
    if (normalizedRecordPath && fromRootArrayNotation) {
      return;
    }
    const sourceArrayPaths = Array.isArray(variable?.arrayPaths) ? variable.arrayPaths : [];
    const normalizedSourceArrayPaths = [...new Set(sourceArrayPaths
      .map((item) => normalizeArrayPathCandidate(item))
      .filter((item) => item !== null))];
    if (fromRootArrayNotation && !normalizedSourceArrayPaths.includes('')) {
      normalizedSourceArrayPaths.unshift('');
    }

    if (!normalizedRecordPath) {
      registerOption(normalizedSourcePath, variable, normalizedSourceArrayPaths);
      return;
    }

    const belongsToSelectedRecord = normalizedSourcePath === normalizedRecordPath
      || normalizedSourcePath.startsWith(`${normalizedRecordPath}.`);

    if (belongsToSelectedRecord) {
      const relativePath = stripExecutionRecordPath(normalizedSourcePath, normalizedRecordPath);
      if (!relativePath) {
        const rootPath = `root.${normalizedRecordPath}`;
        const rootArrayPaths = normalizedSourceArrayPaths.map((path) => (path ? `root.${path}` : 'root'));
        registerOption(rootPath, variable, rootArrayPaths);
        return;
      }
      const relativeArrayPaths = normalizedSourceArrayPaths
        .filter((path) => path === normalizedRecordPath || path.startsWith(`${normalizedRecordPath}.`))
        .map((path) => stripExecutionRecordPath(path, normalizedRecordPath));
      registerOption(relativePath, variable, relativeArrayPaths);
      return;
    }

    if (shouldExposeRootNamespace) {
      const rootPath = `root.${normalizedSourcePath}`;
      const rootArrayPaths = normalizedSourceArrayPaths.map((path) => (path ? `root.${path}` : 'root'));
      registerOption(rootPath, variable, rootArrayPaths);
    }
  });

  return [...mapped.values()].sort((left, right) => String(left.value || '').localeCompare(String(right.value || ''), 'pt-BR'));
};

module.exports = {
  normalizeExecutionStartVariableOptions,
  stripExecutionArrayPrefix,
  stripExecutionRecordPath
};
